"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.sigmaModelEntryFormLatest = exports.SigmaModelEntryFormLatest = exports.SIGMA_MODEL_ENTRY_FORM_STATE_CODEC = exports.UNBOUND_MODEL_SIGNAL_ID = exports.SigmaModelEntryFormDataLatestSchema = exports.SigmaModelEntryFormStateSchema = exports.FormPinAssignmentSchema = exports.PinBlockRefSchema = exports.SigmaModelEntryFormStateCodec = exports.BoundSigmaModelEntryForm = exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION = void 0;
exports.isUnboundPinAssignment = isUnboundPinAssignment;
exports.flagOperatorAddedPinAssignments = flagOperatorAddedPinAssignments;
exports.payloadToFormState = payloadToFormState;
exports.formStateToCanonical = formStateToCanonical;
exports.payloadToSimpleFormState = payloadToSimpleFormState;
exports.simpleFormStateToCanonical = simpleFormStateToCanonical;
exports.payloadToRepeatableBlockFormState = payloadToRepeatableBlockFormState;
exports.repeatableBlockFormStateToCanonical = repeatableBlockFormStateToCanonical;
exports.payloadToArrayFormState = payloadToArrayFormState;
exports.arrayFormStateToCanonical = arrayFormStateToCanonical;
exports.blockRefFor = blockRefFor;
exports.buildInitialFormState = buildInitialFormState;
exports.remapSeedPinoutForModelSignals = remapSeedPinoutForModelSignals;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const z = require("zod");
const CadstromUid_1 = require("../../../../../../CadstromUid");
const generate_readable_id_1 = require("../../../../../../GenerateReadableId/generate-readable-id");
const array_submodels_1 = require("../../../../../../PackageStore/ComponentArrays/array-submodels");
const misc_1 = require("../../../../misc");
const array_resolution_adapter_1 = require("../array-resolution-adapter");
const form_model_shape_1 = require("../form-model-shape");
const no_connect_signal_1 = require("../no-connect-signal");
const pin_count_rules_1 = require("../pin-count-rules");
exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION = 'sigma-model-entry-form/v2';
class BoundSigmaModelEntryForm {
    constructor(resolver) {
        this.resolver = resolver;
    }
    toFormData(payload) {
        return payloadToFormState(payload, this.resolver);
    }
    toCanonical(formState) {
        return formStateToCanonical(formState, this.resolver);
    }
    initForClass(sigmaModelType, seed = null) {
        return buildInitialFormState(sigmaModelType, this.resolver, seed);
    }
}
exports.BoundSigmaModelEntryForm = BoundSigmaModelEntryForm;
class SigmaModelEntryFormStateCodec {
    constructor() {
        this.version = exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION;
        this.formDataSchema = exports.SigmaModelEntryFormStateSchema;
    }
    bind(resolver) {
        return new BoundSigmaModelEntryForm(resolver);
    }
}
exports.SigmaModelEntryFormStateCodec = SigmaModelEntryFormStateCodec;
exports.SigmaModelEntryFormLatest = SigmaModelEntryFormStateCodec;
exports.PinBlockRefSchema = z.object({
    name: z.string().min(1),
    index: z.number().int().nonnegative(),
});
exports.FormPinAssignmentSchema = z.object({
    modelSignalId: z.string(),
    block: exports.PinBlockRefSchema.nullable(),
    nameOverride: z.string().nullable(),
    pins: z.string().array(),
    packageSignalId: z.string().optional(),
    floatingBehavior: sigma_engine_shared_1.DesignModels.SignalFloatingBehaviorSchema.optional(),
});
exports.SigmaModelEntryFormStateSchema = z.object({
    formVersion: z.literal(exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION),
    parentSigmaModelType: z.string(),
    elementSigmaModelType: z.string().nullable(),
    pinCount: z.number().int().nullable(),
    arrayConfirmed: z.boolean().nullable(),
    pinAssignments: exports.FormPinAssignmentSchema.array(),
    ncPins: z.string().array(),
    ncFloatingBehavior: sigma_engine_shared_1.DesignModels.SignalFloatingBehaviorSchema.optional(),
    parameters: misc_1.pkgParametersSchema,
});
exports.SigmaModelEntryFormDataLatestSchema = exports.SigmaModelEntryFormStateSchema;
exports.UNBOUND_MODEL_SIGNAL_ID = '';
function isUnboundPinAssignment(assignment) {
    return assignment.modelSignalId === exports.UNBOUND_MODEL_SIGNAL_ID;
}
function flagOperatorAddedPinAssignments(assignments) {
    const seen = new Set();
    return assignments.map((assignment) => {
        if (isUnboundPinAssignment(assignment)) {
            return true;
        }
        const key = assignment.block === null ?
            assignment.modelSignalId
            : `${assignment.block.name}:${assignment.block.index}:${assignment.modelSignalId}`;
        if (seen.has(key)) {
            return true;
        }
        seen.add(key);
        return false;
    });
}
function payloadToFormState(payload, resolver) {
    const arrayForm = (0, array_resolution_adapter_1.readArrayFormFromResolution)({
        sigmaModelId: payload.sigmaModelType,
        spiceModels: payload.spiceModels,
    });
    if (arrayForm !== null) {
        return payloadToArrayFormState(payload, resolver);
    }
    const definition = resolver(payload.sigmaModelType);
    if (definition !== null && definition.repeatableBlocks.length > 0) {
        return payloadToRepeatableBlockFormState(payload, definition);
    }
    return payloadToSimpleFormState(payload, definition);
}
function formStateToCanonical(formState, resolver) {
    if (formState.elementSigmaModelType !== null) {
        return arrayFormStateToCanonical(formState, resolver);
    }
    const definition = resolver(formState.parentSigmaModelType);
    if (definition !== null && definition.repeatableBlocks.length > 0) {
        return repeatableBlockFormStateToCanonical(formState, definition);
    }
    return simpleFormStateToCanonical(formState, definition);
}
function payloadToSimpleFormState(payload, definition) {
    const pinAssignments = definition !== null ?
        seedSimpleAssignments(payload, definition)
        : payload.signals
            .filter((signal) => !(0, no_connect_signal_1.isNoConnectSignal)(signal))
            .map((signal) => {
            const modelSignalId = modelSignalIdOf(signal);
            return withFloatingBehaviorChoice({
                modelSignalId,
                block: null,
                nameOverride: null,
                pins: [...(payload.pinout[modelSignalId] ?? payload.pinout[String(signal.id)] ?? [])],
            }, (0, form_model_shape_1.recoverFloatingBehaviorChoice)(undefined, signal.floatingBehavior));
        });
    return {
        formVersion: exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION,
        parentSigmaModelType: payload.sigmaModelType,
        elementSigmaModelType: null,
        pinCount: (0, pin_count_rules_1.resolveSeedPinCount)(definition, payload.pinout),
        arrayConfirmed: null,
        pinAssignments,
        ncPins: (0, no_connect_signal_1.readNoConnectPinsFromPayload)(payload),
        ...noConnectFloatingBehaviorField((0, no_connect_signal_1.readNoConnectFloatingBehaviorFromPayload)(payload)),
        parameters: payload.parameters,
    };
}
function seedSimpleAssignments(payload, definition) {
    const modelSignalById = new Map(definition.signals.map((modelSignal) => [String(modelSignal.id), modelSignal]));
    const storedSignalByModelSignalId = new Map();
    const added = [];
    for (const signal of payload.signals) {
        if ((0, no_connect_signal_1.isNoConnectSignal)(signal)) {
            continue;
        }
        const modelSignalId = modelSignalIdOf(signal);
        const modelSignal = modelSignalById.get(modelSignalId);
        if (modelSignal === undefined) {
            if (!hasModelSignalRef(signal)) {
                added.push(unboundAssignment(signal, null, payload.pinout));
            }
            continue;
        }
        if (!storedSignalByModelSignalId.has(modelSignalId)) {
            storedSignalByModelSignalId.set(modelSignalId, signal);
            continue;
        }
        added.push(withFloatingBehaviorChoice({
            modelSignalId,
            block: null,
            nameOverride: signal.name,
            pins: [...(payload.pinout[String(signal.id)] ?? [])],
            packageSignalId: String(signal.id),
        }, (0, form_model_shape_1.recoverFloatingBehaviorChoice)(modelSignal, signal.floatingBehavior)));
    }
    const declared = definition.signals.map((modelSignal) => {
        const modelSignalId = String(modelSignal.id);
        const storedSignal = storedSignalByModelSignalId.get(modelSignalId);
        const nameOverride = storedSignal !== undefined && storedSignal.name !== modelSignal.name ? storedSignal.name : null;
        const pins = (storedSignal !== undefined ? payload.pinout[String(storedSignal.id)] : undefined) ??
            payload.pinout[modelSignalId] ??
            [];
        return withFloatingBehaviorChoice({ modelSignalId, block: null, nameOverride, pins: [...pins] }, (0, form_model_shape_1.recoverFloatingBehaviorChoice)(modelSignal, storedSignal?.floatingBehavior));
    });
    return [...declared, ...added];
}
function simpleFormStateToCanonical(formState, definition) {
    const modelSignalById = new Map((definition?.signals ?? []).map((modelSignal) => [String(modelSignal.id), modelSignal]));
    const added = flagOperatorAddedPinAssignments(formState.pinAssignments);
    const signals = [];
    const pinout = {};
    for (const [index, assignment] of formState.pinAssignments.entries()) {
        if (assignment.block !== null) {
            continue;
        }
        const isAdded = added[index] === true;
        const modelSignal = modelSignalById.get(assignment.modelSignalId);
        const pins = (0, pin_count_rules_1.normalizePinDesignators)(assignment.pins);
        if (isUnpinnedOptional(modelSignal, pins) || (isAdded && pins.length === 0)) {
            continue;
        }
        if (isUnboundPinAssignment(assignment)) {
            const signal = unboundCanonicalSignal(assignment);
            signals.push(signal);
            pinout[String(signal.id)] = pins;
            continue;
        }
        const addedId = isAdded ?
            (assignment.packageSignalId ??
                freshSignalId(assignment.nameOverride ?? modelSignal?.name ?? assignment.modelSignalId))
            : undefined;
        signals.push(modelSignal !== undefined ?
            (0, form_model_shape_1.buildCanonicalSignalFromModelSignal)(modelSignal, {
                ...(addedId !== undefined ? { id: addedId } : {}),
                name: assignment.nameOverride,
                floatingBehavior: assignment.floatingBehavior,
            })
            : fallbackSignal(addedId ?? assignment.modelSignalId, assignment.nameOverride ?? assignment.modelSignalId, assignment.floatingBehavior));
        pinout[addedId ?? assignment.modelSignalId] = pins;
    }
    appendNoConnectSignal(signals, pinout, formState.ncPins, formState.ncFloatingBehavior);
    return {
        sigmaModelType: formState.parentSigmaModelType,
        parameters: formState.parameters,
        signals,
        pinout,
        spiceModels: [],
    };
}
function payloadToRepeatableBlockFormState(payload, definition) {
    const modelSignalById = new Map((definition?.signals ?? []).map((modelSignal) => [String(modelSignal.id), modelSignal]));
    const modelSignalOrder = new Map((definition?.signals ?? []).map((modelSignal, index) => [String(modelSignal.id), index]));
    const groupOrder = [];
    const entriesByGroup = new Map();
    const blockByGroup = new Map();
    for (const signal of payload.signals) {
        if ((0, no_connect_signal_1.isNoConnectSignal)(signal)) {
            continue;
        }
        const modelSignalId = modelSignalIdOf(signal);
        const modelSignal = modelSignalById.get(modelSignalId);
        const isUnbound = definition !== null && modelSignal === undefined && !hasModelSignalRef(signal);
        if (definition !== null && modelSignal === undefined && !isUnbound) {
            continue;
        }
        const block = blockRefOf(signal, modelSignal);
        const groupKey = block !== null ? `${block.name}:${block.index}` : String(signal.id);
        const assignment = isUnbound ?
            unboundAssignment(signal, block, payload.pinout)
            : withFloatingBehaviorChoice({
                modelSignalId,
                block,
                nameOverride: modelSignal !== undefined && signal.name !== modelSignal.name ? signal.name : null,
                pins: [...(payload.pinout[String(signal.id)] ?? [])],
                packageSignalId: String(signal.id),
            }, (0, form_model_shape_1.recoverFloatingBehaviorChoice)(modelSignal, signal.floatingBehavior));
        const signalOrder = modelSignalOrder.get(modelSignalId) ?? Number.MAX_SAFE_INTEGER;
        const existing = entriesByGroup.get(groupKey);
        if (existing === undefined) {
            groupOrder.push(groupKey);
            entriesByGroup.set(groupKey, [{ assignment, signalOrder }]);
            blockByGroup.set(groupKey, block);
        }
        else {
            existing.push({ assignment, signalOrder });
        }
    }
    const pinAssignments = [...groupOrder]
        .sort((left, right) => compareRepeatableBlocks(blockByGroup.get(left) ?? null, blockByGroup.get(right) ?? null))
        .flatMap((groupKey) => (entriesByGroup.get(groupKey) ?? [])
        .sort((a, b) => a.signalOrder - b.signalOrder)
        .map((entry) => entry.assignment));
    return {
        formVersion: exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION,
        parentSigmaModelType: payload.sigmaModelType,
        elementSigmaModelType: null,
        pinCount: (0, pin_count_rules_1.resolveSeedPinCount)(definition, payload.pinout),
        arrayConfirmed: null,
        pinAssignments,
        ncPins: (0, no_connect_signal_1.readNoConnectPinsFromPayload)(payload),
        ...noConnectFloatingBehaviorField((0, no_connect_signal_1.readNoConnectFloatingBehaviorFromPayload)(payload)),
        parameters: payload.parameters,
    };
}
function repeatableBlockFormStateToCanonical(formState, definition) {
    const modelSignalById = new Map((definition?.signals ?? []).map((modelSignal) => [String(modelSignal.id), modelSignal]));
    const added = flagOperatorAddedPinAssignments(formState.pinAssignments);
    const signals = [];
    const pinout = {};
    for (const [index, assignment] of formState.pinAssignments.entries()) {
        const modelSignal = modelSignalById.get(assignment.modelSignalId);
        const pins = (0, pin_count_rules_1.normalizePinDesignators)(assignment.pins);
        if (isUnpinnedOptional(modelSignal, pins) || (added[index] === true && pins.length === 0)) {
            continue;
        }
        if (isUnboundPinAssignment(assignment)) {
            const signal = unboundCanonicalSignal(assignment);
            signals.push(signal);
            pinout[String(signal.id)] = pins;
            continue;
        }
        const effectiveName = assignment.nameOverride ?? modelSignal?.name ?? assignment.modelSignalId;
        const blockInfo = assignment.block !== null && assignment.block.name !== 'pwr' ? { index: assignment.block.index } : undefined;
        const id = assignment.packageSignalId ??
            freshSignalId(assignment.block !== null && blockInfo !== undefined ?
                (0, array_submodels_1.composeArraySignalName)(`${assignment.block.name}${String(assignment.block.index + 1)}`, effectiveName)
                : effectiveName);
        signals.push(modelSignal !== undefined ?
            (0, form_model_shape_1.buildCanonicalSignalFromModelSignal)(modelSignal, {
                name: assignment.nameOverride,
                id,
                blockInfo,
                floatingBehavior: assignment.floatingBehavior,
            })
            : fallbackSignal(id, effectiveName, assignment.floatingBehavior));
        pinout[id] = pins;
    }
    appendNoConnectSignal(signals, pinout, formState.ncPins, formState.ncFloatingBehavior);
    return {
        sigmaModelType: formState.parentSigmaModelType,
        parameters: formState.parameters,
        signals,
        pinout,
        spiceModels: [],
    };
}
function payloadToArrayFormState(payload, resolver) {
    const arrayForm = (0, array_resolution_adapter_1.readArrayFormFromResolution)({
        sigmaModelId: payload.sigmaModelType,
        spiceModels: payload.spiceModels,
    });
    const elementType = arrayForm?.elementModelType ?? payload.sigmaModelType;
    const prefix = arrayForm?.submodelPrefix ?? (0, array_submodels_1.getArrayElementConfig)(elementType)?.prefix ?? '';
    const elementCount = arrayForm?.elementCount ?? 0;
    const elementDefinition = resolver(elementType);
    const signalById = new Map(payload.signals.map((signal) => [String(signal.id), signal]));
    const connectionByKey = new Map();
    for (const submodel of payload.spiceModels.filter(sigma_engine_shared_1.DesignModels.SPICE.isSubModelSigmaSubModel)) {
        const index = parseSubModelIndex(submodel.subModelId);
        for (const connection of submodel.modelInfo.signalConnectionMap) {
            connectionByKey.set(`${index}:${String(connection.modelSignalId)}`, {
                packageSignalId: String(connection.componentSignalId),
                pins: [...(payload.pinout[String(connection.componentSignalId)] ?? [])],
            });
        }
    }
    const connectedSignalIds = new Set([...connectionByKey.values()].map((connection) => connection.packageSignalId));
    const unboundByElement = new Map();
    for (const signal of payload.signals) {
        if ((0, no_connect_signal_1.isNoConnectSignal)(signal) || connectedSignalIds.has(String(signal.id))) {
            continue;
        }
        const index = signal.blockInfo?.index ?? 0;
        const assignment = unboundAssignment(signal, { name: prefix, index }, payload.pinout);
        unboundByElement.set(index, [...(unboundByElement.get(index) ?? []), assignment]);
    }
    const pinAssignments = [];
    const terminals = elementDefinition?.signals ?? [];
    if (terminals.length > 0) {
        for (let index = 0; index < elementCount; index += 1) {
            for (const terminal of terminals) {
                const connection = connectionByKey.get(`${index}:${String(terminal.id)}`);
                const submodelId = `${prefix}${index + 1}`;
                const structuralName = (0, array_submodels_1.composeArraySignalName)(submodelId, terminal.name);
                const storedSignal = connection !== undefined ? signalById.get(connection.packageSignalId) : undefined;
                const nameOverride = storedSignal !== undefined && storedSignal.name !== structuralName ? storedSignal.name : null;
                pinAssignments.push(withFloatingBehaviorChoice({
                    modelSignalId: String(terminal.id),
                    block: { name: prefix, index },
                    nameOverride,
                    pins: connection?.pins ?? [],
                    ...(connection !== undefined ? { packageSignalId: connection.packageSignalId } : {}),
                }, (0, form_model_shape_1.recoverFloatingBehaviorChoice)(terminal, storedSignal?.floatingBehavior)));
            }
            pinAssignments.push(...(unboundByElement.get(index) ?? []));
            unboundByElement.delete(index);
        }
    }
    else {
        for (const [key, connection] of connectionByKey) {
            const [indexText, modelSignalId] = key.split(':');
            pinAssignments.push({
                modelSignalId: modelSignalId ?? '',
                block: { name: prefix, index: Number.parseInt(indexText ?? '0', 10) },
                nameOverride: null,
                pins: connection.pins,
                packageSignalId: connection.packageSignalId,
            });
        }
    }
    for (const index of [...unboundByElement.keys()].sort((left, right) => left - right)) {
        pinAssignments.push(...(unboundByElement.get(index) ?? []));
    }
    return {
        formVersion: exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION,
        parentSigmaModelType: payload.sigmaModelType,
        elementSigmaModelType: elementType,
        pinCount: (0, pin_count_rules_1.derivePhysicalPinCount)(payload.pinout),
        arrayConfirmed: true,
        pinAssignments,
        ncPins: (0, no_connect_signal_1.readNoConnectPinsFromPayload)(payload),
        ...noConnectFloatingBehaviorField((0, no_connect_signal_1.readNoConnectFloatingBehaviorFromPayload)(payload)),
        parameters: (0, array_resolution_adapter_1.readArrayElementParametersFromSubModels)(payload.spiceModels),
    };
}
function arrayFormStateToCanonical(formState, resolver) {
    const elementType = formState.elementSigmaModelType ?? formState.parentSigmaModelType;
    const config = (0, array_submodels_1.getArrayElementConfig)(elementType);
    const prefix = config?.prefix ?? '';
    const elementDefinition = resolver(elementType);
    const terminalById = new Map((elementDefinition?.signals ?? []).map((signal) => [String(signal.id), signal]));
    const added = flagOperatorAddedPinAssignments(formState.pinAssignments);
    const signals = [];
    const compositeSignals = [];
    const pinout = {};
    let maxIndex = -1;
    for (const [index, assignment] of formState.pinAssignments.entries()) {
        if (assignment.block === null) {
            continue;
        }
        const pins = (0, pin_count_rules_1.normalizePinDesignators)(assignment.pins);
        if (isUnboundPinAssignment(assignment)) {
            if (pins.length === 0) {
                continue;
            }
            const signal = unboundCanonicalSignal(assignment);
            signals.push(signal);
            pinout[String(signal.id)] = pins;
            continue;
        }
        maxIndex = Math.max(maxIndex, assignment.block.index);
        const terminal = terminalById.get(assignment.modelSignalId);
        if (isUnpinnedOptional(terminal, pins) || (added[index] === true && pins.length === 0)) {
            continue;
        }
        const submodelId = `${prefix}${assignment.block.index + 1}`;
        const terminalName = terminal?.name ?? assignment.modelSignalId;
        const structuralName = (0, array_submodels_1.composeArraySignalName)(submodelId, terminalName);
        const name = assignment.nameOverride ?? structuralName;
        const id = assignment.packageSignalId ?? freshSignalId(structuralName);
        signals.push(buildArrayCompositeSignal({
            terminal,
            submodelId,
            terminalName,
            name,
            id,
            floatingBehavior: assignment.floatingBehavior,
        }));
        compositeSignals.push({ id, name: structuralName });
        pinout[id] = pins;
    }
    const spiceModels = elementDefinition !== null ?
        (0, array_resolution_adapter_1.buildArrayFormSpiceModels)({
            arrayForm: {
                confirmed: true,
                elementModelType: elementType,
                elementCount: maxIndex + 1,
                submodelPrefix: prefix,
            },
            signals: compositeSignals,
            parameters: formState.parameters,
            parameterDefs: elementDefinition.parameters,
            elementSignals: elementDefinition.signals.map((signal) => ({
                id: signal.id,
                name: signal.name,
                isOptional: signal.isOptional,
            })),
        })
        : [];
    appendNoConnectSignal(signals, pinout, formState.ncPins, formState.ncFloatingBehavior);
    return {
        sigmaModelType: config?.parentType ?? formState.parentSigmaModelType,
        parameters: {},
        signals,
        pinout,
        spiceModels,
    };
}
function modelSignalIdOf(signal) {
    return String(signal.modelSignal ?? signal.id);
}
function hasModelSignalRef(signal) {
    return signal.modelSignal !== undefined && signal.modelSignal !== null;
}
function freshSignalId(name) {
    const id = (0, generate_readable_id_1.generateReadableId)(CadstromUid_1.CadstromObjectType.Signal, name, 0);
    return id === no_connect_signal_1.NO_CONNECT_SIGNAL_ID ? `${id}_signal` : id;
}
function freshUnboundSignalId(name) {
    return `${(0, generate_readable_id_1.generateReadableId)(CadstromUid_1.CadstromObjectType.Signal, name, 0)}__added`;
}
function unboundAssignment(signal, block, pinout) {
    return withFloatingBehaviorChoice({
        modelSignalId: exports.UNBOUND_MODEL_SIGNAL_ID,
        block,
        nameOverride: signal.name,
        pins: [...(pinout[String(signal.id)] ?? [])],
        packageSignalId: String(signal.id),
    }, (0, form_model_shape_1.recoverFloatingBehaviorChoice)(undefined, signal.floatingBehavior));
}
function unboundCanonicalSignal(assignment) {
    const name = assignment.nameOverride ?? '';
    return (0, form_model_shape_1.buildUnboundCanonicalSignal)({
        id: assignment.packageSignalId ?? freshUnboundSignalId(name),
        name,
        floatingBehavior: assignment.floatingBehavior,
        blockInfo: assignment.block !== null ? { name: assignment.block.name, index: assignment.block.index } : undefined,
    });
}
function fallbackSignal(modelSignalId, name, floatingBehavior) {
    return {
        id: modelSignalId,
        name,
        padRefs: [],
        pinRefs: [],
        modelSignal: modelSignalId,
        ...(0, form_model_shape_1.floatingBehaviorField)(floatingBehavior),
    };
}
function withFloatingBehaviorChoice(assignment, floatingBehavior) {
    return floatingBehavior !== undefined ? { ...assignment, floatingBehavior } : assignment;
}
function noConnectFloatingBehaviorField(ncFloatingBehavior) {
    return ncFloatingBehavior !== undefined ? { ncFloatingBehavior } : {};
}
function appendNoConnectSignal(signals, pinout, ncPins, ncFloatingBehavior) {
    const pins = (0, pin_count_rules_1.normalizePinDesignators)(ncPins);
    if (pins.length === 0) {
        return;
    }
    const ncSignal = (0, no_connect_signal_1.buildNoConnectSignal)(ncFloatingBehavior);
    signals.push(ncSignal);
    pinout[String(ncSignal.id)] = pins;
}
function isUnpinnedOptional(modelSignal, pins) {
    return modelSignal !== undefined && modelSignal.isOptional && pins.length === 0;
}
function blockRefFor(name, index) {
    if (name === null || name === '') {
        return null;
    }
    return { name, index: name === 'pwr' ? 0 : Math.max(index, 0) };
}
function blockRefOf(signal, modelSignal) {
    return blockRefFor(signal.blockInfo?.name ?? modelSignal?.repeatableBlock ?? null, signal.blockInfo?.index ?? 0);
}
function compareRepeatableBlocks(left, right) {
    const leftIsPwr = left?.name === 'pwr';
    const rightIsPwr = right?.name === 'pwr';
    if (leftIsPwr !== rightIsPwr) {
        return leftIsPwr ? -1 : 1;
    }
    const nameCompare = (left?.name ?? '').localeCompare(right?.name ?? '', undefined, {
        numeric: true,
        sensitivity: 'base',
    });
    return nameCompare !== 0 ? nameCompare : (left?.index ?? 0) - (right?.index ?? 0);
}
function parseSubModelIndex(subModelId) {
    const ref = (0, array_submodels_1.parseSubModelId)(subModelId);
    return ref !== null ? ref.ordinal - 1 : 0;
}
function buildArrayCompositeSignal(args) {
    const { terminal, submodelId, terminalName, name, id, floatingBehavior } = args;
    return {
        id,
        name,
        padRefs: [],
        pinRefs: [],
        signalDirection: terminal?.direction ?? undefined,
        attributes: { ...(terminal?.attributes ?? {}) },
        description: `${submodelId} - ${terminalName}`,
        ...(0, form_model_shape_1.floatingBehaviorField)((0, form_model_shape_1.resolveFormFloatingBehavior)(terminal, floatingBehavior)),
        supportedDriveModes: [],
        ties: [],
        isCadstromInternal: false,
    };
}
function buildInitialFormState(sigmaModelType, resolver, seed = null) {
    const definition = resolver(sigmaModelType);
    const matchesSeed = seed !== null && seed.sigmaModelType !== '' && sigmaModelType === seed.sigmaModelType;
    const classInferredPinCount = (0, pin_count_rules_1.inferPinCountFromDefinition)(definition);
    if (matchesSeed && seed !== null && definition !== null && definition.repeatableBlocks.length > 0) {
        return payloadToRepeatableBlockFormState(seed, definition);
    }
    const seedAdded = matchesSeed && seed !== null && definition !== null ? seedOperatorAddedAssignments(seed, definition) : [];
    const pinoutForClass = definition === null ? {} : resolveInitialPinout(definition, classInferredPinCount, seed, matchesSeed, seedAdded);
    const seedSignalByModelSignalId = matchesSeed && seed !== null ? buildSeedSignalByModelSignalId(seed) : new Map();
    const ncPins = matchesSeed && seed !== null ? (0, no_connect_signal_1.readNoConnectPinsFromPayload)(seed) : [];
    const pinAssignments = definition === null || definition.repeatableBlocks.length > 0 ?
        []
        : [
            ...definition.signals.map((modelSignal) => {
                const modelSignalId = String(modelSignal.id);
                const seedSignal = seedSignalByModelSignalId.get(modelSignalId);
                const nameOverride = seedSignal !== undefined && seedSignal.name !== modelSignal.name ? seedSignal.name : null;
                return withFloatingBehaviorChoice({ modelSignalId, block: null, nameOverride, pins: [...(pinoutForClass[modelSignalId] ?? [])] }, (0, form_model_shape_1.recoverFloatingBehaviorChoice)(modelSignal, seedSignal?.floatingBehavior));
            }),
            ...seedAdded,
        ];
    return {
        formVersion: exports.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION,
        parentSigmaModelType: sigmaModelType,
        elementSigmaModelType: null,
        pinCount: (0, pin_count_rules_1.resolveSeedPinCount)(definition, pinoutForClass, [
            ...ncPins,
            ...seedAdded.flatMap((assignment) => assignment.pins),
        ]),
        arrayConfirmed: null,
        pinAssignments,
        ncPins,
        ...noConnectFloatingBehaviorField(matchesSeed && seed !== null ? (0, no_connect_signal_1.readNoConnectFloatingBehaviorFromPayload)(seed) : undefined),
        parameters: matchesSeed && seed !== null ? seed.parameters : {},
    };
}
function remapSeedPinoutForModelSignals(seedPinout, seedSignals, modelSignals) {
    const pinout = {};
    const modelSignalIds = new Set(modelSignals.map((modelSignal) => String(modelSignal.id)));
    const appendPins = (modelSignalId, pins) => {
        if (!modelSignalIds.has(modelSignalId)) {
            return;
        }
        const nonEmpty = (0, pin_count_rules_1.normalizePinDesignators)(pins);
        if (nonEmpty.length === 0) {
            return;
        }
        pinout[modelSignalId] = [...new Set([...(pinout[modelSignalId] ?? []), ...nonEmpty])];
    };
    const seedSignalIds = new Set(seedSignals.map((seedSignal) => String(seedSignal.id)));
    for (const seedSignal of seedSignals) {
        const { modelSignal } = seedSignal;
        const hasExplicitTarget = (typeof modelSignal === 'string' && modelSignal !== '') || typeof modelSignal === 'number';
        const target = hasExplicitTarget ? String(modelSignal) : String(seedSignal.id);
        appendPins(target, seedPinout[String(seedSignal.id)] ?? []);
    }
    for (const [seedSignalId, pins] of Object.entries(seedPinout)) {
        if (seedSignalIds.has(seedSignalId)) {
            continue;
        }
        appendPins(seedSignalId, pins);
    }
    return pinout;
}
function modelHasImplicitTwoPinLayout(modelSignals) {
    return modelSignals.length === 2 && modelSignals.every((signal) => !signal.isOptional);
}
function buildImplicitTwoPinPinout(modelSignals) {
    const pinout = {};
    const [first, second] = modelSignals;
    if (first !== undefined) {
        pinout[String(first.id)] = ['1'];
    }
    if (second !== undefined) {
        pinout[String(second.id)] = ['2'];
    }
    return pinout;
}
function resolveInitialPinout(definition, classInferredPinCount, seed, matchesSeed, seedAdded) {
    const seedPhysicalPinCount = matchesSeed && seed !== null ? (0, pin_count_rules_1.derivePhysicalPinCount)(seed.pinout) : null;
    if (classInferredPinCount === 2 &&
        modelHasImplicitTwoPinLayout(definition.signals) &&
        (seedPhysicalPinCount === null || seedPhysicalPinCount === 2)) {
        return buildImplicitTwoPinPinout(definition.signals);
    }
    if (matchesSeed && seed !== null) {
        const seedTerminals = withoutSeedSignals(seed, seedAdded);
        const remapped = remapSeedPinoutForModelSignals(seedTerminals.pinout, seedTerminals.signals, definition.signals);
        if (classInferredPinCount === null || (0, pin_count_rules_1.derivePhysicalPinCount)(remapped) === classInferredPinCount) {
            return remapped;
        }
    }
    return {};
}
function seedOperatorAddedAssignments(seed, definition) {
    const seeded = seedSimpleAssignments(seed, definition);
    const added = flagOperatorAddedPinAssignments(seeded);
    return seeded.filter((_, index) => added[index] === true);
}
function withoutSeedSignals(seed, assignments) {
    const dropped = new Set(assignments.flatMap((assignment) => assignment.packageSignalId ?? []));
    if (dropped.size === 0) {
        return seed;
    }
    return {
        ...seed,
        signals: seed.signals.filter((signal) => !dropped.has(String(signal.id))),
        pinout: Object.fromEntries(Object.entries(seed.pinout).filter(([signalId]) => !dropped.has(signalId))),
    };
}
function buildSeedSignalByModelSignalId(seed) {
    const seedSignalByModelSignalId = new Map();
    for (const signal of seed.signals) {
        if (signal.modelSignal === undefined) {
            continue;
        }
        const key = String(signal.modelSignal);
        if (!seedSignalByModelSignalId.has(key)) {
            seedSignalByModelSignalId.set(key, signal);
        }
    }
    return seedSignalByModelSignalId;
}
exports.SIGMA_MODEL_ENTRY_FORM_STATE_CODEC = new SigmaModelEntryFormStateCodec();
exports.sigmaModelEntryFormLatest = exports.SIGMA_MODEL_ENTRY_FORM_STATE_CODEC;
//# sourceMappingURL=form-latest.js.map